import {
    Tag,
    Listbox,
    Combobox,
    Icon,
    InlineStack,
    BlockStack,
    AutoSelection,
} from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { useState, useCallback, useMemo, useEffect } from "react";

interface MultiSelectComboboxProps {
    options: { label: string; value: string }[]; // options as objects
    selected: string[]; // selected values (value)
    onChange: (selected: string[]) => void;
    label?: string;
    placeholder?: string;
    tagRenderer?: (value: string) => string; // new prop
}

export function MultiSelectCombobox({
    options,
    selected,
    onChange,
    label = "Search",
    placeholder = "Search...",
    tagRenderer,
}: MultiSelectComboboxProps) {
    const [inputValue, setInputValue] = useState("");
    const [filteredOptions, setFilteredOptions] = useState(options);
    const selectedOptions = selected;
    

    useEffect(() => {
        if (!inputValue) {
            setFilteredOptions(options);
            return;
        }
        const regex = new RegExp(inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
        setFilteredOptions(options.filter((o) => o.label.match(regex)));
    }, [inputValue, options]);

    const updateSelection = useCallback(
        (value: string) => {
          const newSelected = selectedOptions.includes(value)
            ? selectedOptions.filter((v) => v !== value)
            : [...selectedOptions, value];
      
          onChange(newSelected);
          setInputValue("");
        },
        [selectedOptions, onChange]
      );

      const removeTag = useCallback(
        (value: string) => () => {
          const newSelected = selectedOptions.filter((v) => v !== value);
          onChange(newSelected);
        },
        [selectedOptions, onChange]
      );

    return (
        <BlockStack gap="200">
            <Combobox
                allowMultiple
                activator={
                    <Combobox.TextField
                        prefix={<Icon source={SearchIcon} />}
                        value={inputValue}
                        onChange={setInputValue}
                        label={label}
                        placeholder={placeholder}
                        autoComplete="off"
                    />
                }
            >
                <Listbox autoSelection={AutoSelection.None} onSelect={updateSelection}>
                    {filteredOptions.map(({ label, value }) => (
                        <Listbox.Option key={value} value={value} selected={selectedOptions.includes(value)}>
                            {label}
                        </Listbox.Option>
                    ))}
                </Listbox>
            </Combobox>

            {selectedOptions.length > 0 && (
                <InlineStack gap="200">
                    {selectedOptions.map((value) => (
                        <Tag key={value} onRemove={removeTag(value)}>
                            {tagRenderer ? tagRenderer(value) : value}
                        </Tag>
                    ))}
                </InlineStack>
            )}
        </BlockStack>
    );
}